import { useState } from 'react';

import { useUpdateJobStateMutation } from '@/modules/jobs/mutations/useUpdateJobStateMutation';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import {
  PortalDialog,
  PortalDialogContent,
  PortalDialogDescription,
  PortalDialogFooter,
  PortalDialogHeader,
  PortalDialogTitle,
} from '@/modules/ui/components/portal-dialog/PortalDialog';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/modules/ui/components/radio-group/RadioGroup';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

// Hired and Closed behave the same in the API; the reason only picks the label.
const reasons = [
  { value: 'finlay', label: 'We hired a Finlay candidate', state: 'Hired' },
  { value: 'elsewhere', label: 'We filled it another way', state: 'Hired' },
  {
    value: 'cancelled',
    label: 'We are no longer hiring for this role',
    state: 'Closed',
  },
] as const;

type Reason = (typeof reasons)[number]['value'];

interface CloseJobDialogProps {
  job: JobApi;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The dialog has no trigger of its own, so the caller returns focus. */
  onCloseAutoFocus: (event: Event) => void;
}

/**
 * Asks why the role is closing and moves the job to Hired or Closed. The
 * header's state badge confirms success; a failure stays in the dialog.
 */
export function CloseJobDialog({
  job,
  open,
  onOpenChange,
  onCloseAutoFocus,
}: CloseJobDialogProps) {
  const updateState = useUpdateJobStateMutation();
  const [reason, setReason] = useState<Reason>('finlay');
  const isClosing = updateState.isPending;

  const changeOpen = (next: boolean) => {
    if (isClosing) return;
    // Reopening starts clean instead of showing the last attempt's error.
    if (!next) updateState.reset();
    onOpenChange(next);
  };

  const handleClose = () => {
    const state = reasons.find((option) => option.value === reason)?.state;
    if (!state) return;
    updateState.mutate(
      { jobId: job.id, state },
      { onSuccess: () => changeOpen(false) },
    );
  };

  return (
    <PortalDialog onOpenChange={changeOpen} open={open}>
      <PortalDialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <PortalDialogHeader>
          <PortalDialogTitle>Close {job.title}?</PortalDialogTitle>
          <PortalDialogDescription>
            Finlay stops sourcing and outreach. Closed jobs cannot be reopened.
            What happened with the role?
          </PortalDialogDescription>
        </PortalDialogHeader>
        <RadioGroup
          aria-label="Why is this role closing?"
          className="gap-3"
          onValueChange={(value) => setReason(value as Reason)}
          value={reason}
        >
          {reasons.map((option) => (
            <label
              className="flex items-center gap-3 text-[13px]"
              key={option.value}
            >
              <RadioGroupItem value={option.value} />
              {option.label}
            </label>
          ))}
        </RadioGroup>
        {updateState.isError && (
          <InlineError>
            {actionErrorMessage('close the job', updateState.error)}
          </InlineError>
        )}
        <PortalDialogFooter>
          <Button
            disabled={isClosing}
            onClick={() => changeOpen(false)}
            size="sm"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            loading={isClosing}
            onClick={handleClose}
            size="sm"
            variant="destructive"
          >
            {isClosing ? 'Closing…' : 'Close job'}
          </Button>
        </PortalDialogFooter>
      </PortalDialogContent>
    </PortalDialog>
  );
}
